import { useEffect, useState } from "react";
import { X, Loader2, ChevronDown, ShieldCheck, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { TREND_STYLES } from "@/lib/stateMeta";

const Stat = ({ label, value, accent, testid }) => (
  <div className="text-center px-2" data-testid={testid}>
    <p className={`font-serif leading-none tabular-nums ${accent ? "text-blaze text-4xl" : "text-forest text-3xl"}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-charcoal/45 mt-1.5">{label}</p>
  </div>
);

function Expand({ title, children, testid, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-b border-saddle/15" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between py-4 text-left" data-testid={`${testid}-toggle`}>
        <span className="font-serif text-lg text-forest">{title}</span>
        <ChevronDown size={18} className={`text-saddle transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 text-sm text-charcoal/75">{children}</div>}
    </div>
  );
}

export function HuntDetailDrawer({ huntId, points, onClose }) {
  const [h, setH] = useState(null);
  useEffect(() => { setH(null); if (huntId) api.exploreHuntDetail(huntId, points).then(setH).catch(() => setH(null)); }, [huntId, points]);
  if (!huntId) return null;
  const t = h ? (TREND_STYLES[h.trend?.key] || TREND_STYLES.unknown) : null;

  return (
    <div className="fixed inset-0 z-50" data-testid="hunt-detail-drawer">
      <div className="absolute inset-0 bg-forest/50 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-paper overflow-y-auto" data-testid="drawer-panel">
        {!h ? (
          <div className="h-full grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : (
          <div>
            <div className="sticky top-0 bg-forest text-bone px-6 py-5 flex items-start justify-between z-10">
              <div>
                <p className="hr-overline text-blaze">{h.jurisdiction} · {h.species}</p>
                <h2 className="font-serif text-3xl leading-none tracking-tight mt-1">{h.unit}</h2>
                <p className="text-xs text-bone/60 mt-2">{h.weapon} · {h.residency} · {h.season}</p>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid="drawer-close"><X size={22} className="text-bone/80 hover:text-bone" /></button>
            </div>

            <div className="px-6 py-6">
              <div className="grid grid-cols-3 gap-3 bg-white border border-saddle/20 py-5">
                <Stat label="Draw odds" value={`${h.draw_odds_pct}%`} accent testid="stat-odds" />
                <Stat label="Points" value={h.points_required} testid="stat-points" />
                <Stat label="Point creep" value={h.point_creep} testid="stat-creep" />
              </div>

              <div className="flex items-center gap-2 mt-4">
                <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
                <span className={`text-sm font-medium ${t.text}`}>{h.trend?.label}</span>
              </div>

              {h.personalized && (
                <div className="mt-5 bg-forest text-bone p-5" data-testid="drawer-personal-odds">
                  <div className="flex items-center justify-between">
                    <span className="hr-overline text-blaze">Your estimated odds · {h.personalized.points_used} pts</span>
                    <span className="font-serif text-4xl leading-none tabular-nums">{h.personalized.est_odds_pct}%</span>
                  </div>
                  <p className="text-xs text-bone/70 mt-2">
                    {h.personalized.label}
                    {h.personalized.eligibility === "below" && h.personalized.years_to_typical ? ` · about ${h.personalized.years_to_typical} more year${h.personalized.years_to_typical === 1 ? "" : "s"} to reach the typical point level` : ""}
                    {" "}· Estimate based on official draw data, not a guarantee.
                  </p>
                </div>
              )}

              <div className="mt-6">
                <Expand title="Odds & Point History" testid="section-history" defaultOpen>
                  {h.odds_history?.length ? (
                    <div className="flex items-end gap-2 h-32 mt-2" data-testid="history-chart">
                      {h.odds_history.map((pt) => (
                        <div key={pt.year} className="flex-1 flex flex-col items-center gap-1.5">
                          <div className="w-full bg-blaze/80" style={{ height: `${Math.max(6, pt.pct)}%` }} title={`${pt.pct}%`} />
                          <span className="text-[10px] text-charcoal/50">{String(pt.year).slice(2)}</span>
                          <span className="text-[10px] text-charcoal/70 tabular-nums">{pt.pct}%</span>
                        </div>
                      ))}
                    </div>
                  ) : <p>History not yet available for this hunt.</p>}
                </Expand>
                <Expand title="Season Details" testid="section-season">
                  <p><span className="text-charcoal/50">Season:</span> {h.season}</p>
                  <p className="mt-1"><span className="text-charcoal/50">Weapon:</span> {h.weapon}</p>
                  <p className="mt-1"><span className="text-charcoal/50">Residency:</span> {h.residency}</p>
                </Expand>
                <Expand title="Regulations & Application" testid="section-regs">
                  <p>Applications and full regulations are managed by the state wildlife agency.</p>
                  {h.source?.official_url && (
                    <a href={h.source.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-blaze font-semibold hover:underline" data-testid="regs-official-link">
                      View official regulations <ExternalLink size={13} />
                    </a>
                  )}
                </Expand>
              </div>

              {/* Subtle source line */}
              <div className="mt-6 pt-5 border-t border-saddle/15">
                <div className="flex items-center gap-2 text-sm text-[#2E7D46]">
                  <ShieldCheck size={16} />
                  <span>Verified official state data</span>
                </div>
                <Expand title="Source Details" testid="section-source">
                  <p><span className="text-charcoal/50">Official agency:</span> {h.source?.agency || "State wildlife agency"}</p>
                  <p className="mt-1"><span className="text-charcoal/50">Data year:</span> {h.source?.data_year || "—"}</p>
                  {h.source?.official_url && (
                    <a href={h.source.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-blaze font-semibold hover:underline" data-testid="source-official-link">
                      View official source <ExternalLink size={13} />
                    </a>
                  )}
                </Expand>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
